import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import OrderRealizationsAdjusted from '../components/order-realizations/order-realizations-adjusted';
import { useCheckBalanceHook } from '../hooks/checkBalanceHook';
import { fetchIdOrder } from '../store/actions/order-detailed';
import {
  confirmAdjustmentDoc,
  fetchAdjustmentOrders,
  fetchRealizationPositions,
  initAdjustmentDoc,
  setAdjustmentSort,
} from '../store/actions/order-realization-detailed';
import { sortedAdjustedPositions } from '../store/selectors/order-realization-detaild';
import { RootState } from '../types';
import { ORDER_DETAILED_STATUS } from '../types';

const OrderAdjustedPositionsContainer: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams<{ id: string; itemId: string }>();
  const { checkTokenBalance } = useCheckBalanceHook();
  const auth = useSelector((state: RootState) => state.auth.account);
  const info = useSelector(
    (state: RootState) => state.realizationPositions.data.info
  );
  const order = useSelector(
    (state: RootState) => state.orderDetailed.data.order
  );
  const adjustment = useSelector(
    (state: RootState) => state.realizationPositions.data.adjustment
  );
  const sorts = useSelector(
    (state: RootState) => state.realizationPositions.data.adjustmentSorts
  );
  const sortedPos = useSelector(sortedAdjustedPositions);

  useEffect(() => {
    dispatch(fetchIdOrder({ id: parseInt(params.id, 10) }));
  }, []);

  useEffect(() => {
    dispatch(
      fetchRealizationPositions({
        id: parseInt(params.id, 10),
        itemId: parseInt(params.itemId, 10),
      })
    );
  }, []);

  useEffect(() => {
    if (adjustment?.originalOrderItemId && adjustment.orderItemId) {
      dispatch(
        fetchAdjustmentOrders({
          id: adjustment.orderItemId,
          orderId: parseInt(params.id, 10),
          originalId: adjustment.originalOrderItemId,
        })
      );
    }
  }, [adjustment?.originalOrderItemId, adjustment?.orderItemId]);

  const getDirections = () => ({
    oldCount: sorts.oldCount.direction,
    count: sorts.count.direction,
    sum: sorts.sum.direction,
    oldSum: sorts.oldSum.direction,
  });

  const getAdjustmentType = () => {
    if (adjustment?.status === 'INIT') {
      if (adjustment.fromAccountId === auth.id) return 'payment';
      return 'waiting';
    }
    if (adjustment?.status === 'PENDING') {
      if (adjustment.fromAccountId === auth.id) return 'waiting';
      return 'matching';
    }

    return 'waiting';
  };

  const onPaymentSubmit = () => {
    dispatch(initAdjustmentDoc(adjustment!.id));
    history.push(`/orders/${params.id}/realizations/sign?type=adjustment`);
  };

  const onPaymentConfirm = () => {
    dispatch(confirmAdjustmentDoc(adjustment!.id));
    history.push(`/main`);
  };

  const onSubmit = () => {
    if (getAdjustmentType() === 'payment') {
      onPaymentSubmit();
    }

    if (getAdjustmentType() === 'matching') {
      onPaymentConfirm();
    }
  };

  const getBalanceError = () => {
    if (info.tokenName && adjustment?.difference) {
      return !checkTokenBalance(
        'HYBRID',
        info.tokenName,
        adjustment?.difference
      );
    }
    return false;
  };

  return (
    <OrderRealizationsAdjusted
      orderRef={info.refNumber ?? ''}
      adjustmentComment={info.comment}
      balanceError={getBalanceError()}
      status={order?.status as ORDER_DETAILED_STATUS}
      totalChangedSum={adjustment?.difference ?? 0}
      type={getAdjustmentType()}
      onSubmit={onSubmit}
      onToggleSort={(name, direction) => {
        dispatch(setAdjustmentSort({ name, direction }));
      }}
      directions={getDirections()}
      data={sortedPos}
    />
  );
};

export default OrderAdjustedPositionsContainer;
